import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { t } from '../../i18n';
import styles from './AdLayoutEditor.module.css';
import {
  AD_ELEMENT_KEYS,
  AD_FIXED_TEXT_FIELDS,
  AD_LANGUAGES,
  DEFAULT_TRANSLATION,
  getLayoutBranch,
  getProfilesForDevice,
  isRtlAdLanguage,
  mapLayoutProfiles,
  normalizeAdLanguageKey,
  normalizeContentJson,
  normalizeLayoutJson,
  resolveCustomText,
  updateLayoutBranch,
} from './adLayoutDefaults';

const ELEMENT_LABELS = {
  badge: '배지',
  title: '제목',
  reward: '보상',
  description: '설명',
  button: '버튼',
};

function clamp(value, min, max) {
  const n = Number(value);
  if (!Number.isFinite(n)) return min;
  return Math.min(max, Math.max(min, n));
}

function makeId(prefix) {
  const random = Math.random().toString(36).slice(2, 9);
  return `${prefix}_${Date.now()}_${random}`;
}

function alignStyle(align) {
  if (align === 'center') return { justifyContent: 'center', textAlign: 'center' };
  if (align === 'right') return { justifyContent: 'flex-end', textAlign: 'right' };
  return { justifyContent: 'flex-start', textAlign: 'left' };
}

function resolveTranslation(contentJson, language) {
  const normalized = normalizeContentJson(contentJson);
  const selectedLanguage = normalizeAdLanguageKey(language);
  const familyFallback = selectedLanguage === 'zh-tw' ? normalized.translations.zh : null;
  return {
    ...DEFAULT_TRANSLATION,
    ...(normalized.translations[normalized.defaultLanguage] || {}),
    ...(familyFallback || {}),
    ...(normalized.translations[selectedLanguage] || {}),
  };
}

function useElementWidth(ref) {
  const [width, setWidth] = useState(0);
  useLayoutEffect(() => {
    const node = ref.current;
    if (!node) return undefined;
    const sync = () => setWidth(node.clientWidth || 0);
    sync();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', sync);
      return () => window.removeEventListener('resize', sync);
    }
    const observer = new ResizeObserver(sync);
    observer.observe(node);
    return () => observer.disconnect();
  }, [ref]);
  return width;
}

function PreviewAutoFitText({ children, maxSize, minSize = 4, align = 'left' }) {
  const ref = useRef(null);
  useLayoutEffect(() => {
    const node = ref.current;
    if (!node) return undefined;
    const fit = () => {
      const upper = Math.max(minSize, Number(maxSize) || minSize);
      let low = minSize;
      let high = upper;
      node.style.fontSize = `${upper}px`;
      for (let i = 0; i < 9; i += 1) {
        const mid = (low + high) / 2;
        node.style.fontSize = `${mid}px`;
        const fits = node.scrollWidth <= node.clientWidth + 1 && node.scrollHeight <= node.clientHeight + 1;
        if (fits) low = mid;
        else high = mid;
      }
      node.style.fontSize = `${Math.max(minSize, low)}px`;
    };
    fit();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(fit);
    observer.observe(node);
    return () => observer.disconnect();
  }, [children, maxSize, minSize]);

  return (
    <span
      ref={ref}
      style={{
        display: 'flex', width: '100%', height: '100%', minWidth: 0, alignItems: 'center',
        justifyContent: align === 'center' ? 'center' : align === 'right' ? 'flex-end' : 'flex-start',
        textAlign: align, lineHeight: 1.08, overflow: 'hidden', wordBreak: 'keep-all', overflowWrap: 'anywhere',
      }}
    >
      {children}
    </span>
  );
}

function elementRefType(ref) {
  if (String(ref || '').startsWith('text:')) return 'customText';
  if (String(ref || '').startsWith('image:')) return 'image';
  return 'fixed';
}

function elementRefId(ref) {
  const value = String(ref || '');
  const index = value.indexOf(':');
  return index >= 0 ? value.slice(index + 1) : value;
}

function findElementConfig(branch, ref) {
  const type = elementRefType(ref);
  const id = elementRefId(ref);
  if (type === 'customText') return (branch.customTexts || []).find((item) => item.id === id) || null;
  if (type === 'image') return (branch.imageElements || []).find((item) => item.id === id) || null;
  return branch[id] || null;
}

export function AdLayoutCanvas({
  imageUrl,
  contentJson,
  layoutJson,
  device = 'desktop',
  profile,
  language,
  selectedElement = null,
  onSelectElement,
  onElementChange,
  interactive = false,
  backgroundMode = 'COVER',
  templateKey = 'FREEFORM_V1',
  assetPreviewUrls = {},
  className = '',
}) {
  const canvasRef = useRef(null);
  const dragRef = useRef(null);
  const normalizedLayout = useMemo(() => normalizeLayoutJson(layoutJson), [layoutJson]);
  const branch = useMemo(() => getLayoutBranch(normalizedLayout, device, profile), [normalizedLayout, device, profile]);
  const translation = useMemo(() => resolveTranslation(contentJson, language), [contentJson, language]);
  const ratio = `${branch.canvas?.width || 1167} / ${branch.canvas?.height || 75}`;
  const background = branch.background || {};
  const imageOnly = String(templateKey || 'FREEFORM_V1').toUpperCase() === 'IMAGE_ONLY';
  const rtl = isRtlAdLanguage(language);
  const canvasWidth = useElementWidth(canvasRef);
  const baseWidth = Number(branch.canvas?.width || (device === 'mobile' ? 355 : 1167));
  const fontScale = clamp(canvasWidth && baseWidth ? canvasWidth / baseWidth : 1, 0.65, 2.8);
  const assetMap = useMemo(() => {
    const map = {};
    (normalizedLayout.assets || []).forEach((asset) => { map[asset.id] = asset; });
    return map;
  }, [normalizedLayout.assets]);

  const startDrag = (event, ref) => {
    if (!interactive || !onElementChange) return;
    event.preventDefault();
    event.stopPropagation();
    const rect = canvasRef.current?.getBoundingClientRect();
    const current = findElementConfig(branch, ref);
    if (!rect || !current) return;
    onSelectElement?.(ref);
    dragRef.current = {
      pointerId: event.pointerId, ref, startX: event.clientX, startY: event.clientY,
      originX: Number(current.x || 0), originY: Number(current.y || 0), rect,
    };
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  const moveDrag = (event) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    const current = findElementConfig(branch, drag.ref);
    if (!current) return;
    const dx = ((event.clientX - drag.startX) / drag.rect.width) * 100;
    const dy = ((event.clientY - drag.startY) / drag.rect.height) * 100;
    const width = Number(current.width || 0);
    const height = Number(current.height || 0);
    onElementChange(drag.ref, {
      x: clamp(drag.originX + dx, 0, Math.max(0, 100 - width)),
      y: clamp(drag.originY + dy, 0, Math.max(0, 100 - height)),
    });
  };

  const endDrag = (event) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    dragRef.current = null;
    event.currentTarget.releasePointerCapture?.(event.pointerId);
  };

  const interactionProps = (ref) => ({
    onPointerDown: (event) => startDrag(event, ref),
    onPointerMove: moveDrag,
    onPointerUp: endDrag,
    onPointerCancel: endDrag,
    onClick: (event) => {
      if (!interactive) return;
      event.preventDefault();
      event.stopPropagation();
      onSelectElement?.(ref);
    },
  });

  const renderFixedElement = (key, content) => {
    const cfg = branch[key];
    if (!cfg || cfg.visible === false || imageOnly) return null;
    const scaledSize = Number(cfg.fontSize || 9) * fontScale;
    const commonStyle = {
      left: `${clamp(cfg.x, 0, 100)}%`, top: `${clamp(cfg.y, 0, 100)}%`,
      width: `${clamp(cfg.width, 0, 100)}%`, height: `${clamp(cfg.height, 0, 100)}%`,
      color: cfg.color || '#fff', fontWeight: cfg.fontWeight || 800,
      fontSize: key === 'badge' ? `${scaledSize}px` : undefined,
      minWidth: interactive ? 14 : 1, minHeight: interactive ? 10 : 1,
      cursor: interactive ? 'grab' : 'default', ...alignStyle(cfg.align),
    };
    const classNames = [
      styles.element, selectedElement === key ? styles.elementSelected : '',
      key === 'badge' ? styles.badge : '', key === 'button' ? styles.buttonElement : styles.textElement,
    ].filter(Boolean).join(' ');

    let renderedContent;
    if (key === 'badge') {
      renderedContent = <><span className={styles.badgeTop}>{translation.badgeTop}</span><strong className={styles.badgeBottom}>{translation.badgeBottom}</strong></>;
    } else if (key === 'button') {
      renderedContent = <><PreviewAutoFitText maxSize={scaledSize} minSize={Math.max(4, scaledSize * .55)} align={cfg.align || 'center'}>{translation.buttonText}</PreviewAutoFitText><i className={styles.buttonArrow}>›</i></>;
    } else {
      renderedContent = <PreviewAutoFitText maxSize={scaledSize} minSize={Math.max(3.5, scaledSize * .48)} align={cfg.align || 'left'}>{content}</PreviewAutoFitText>;
    }

    return <div key={key} className={classNames} style={commonStyle} {...interactionProps(key)}>{renderedContent}</div>;
  };

  const renderCustomText = (cfg) => {
    if (!cfg || cfg.visible === false || imageOnly) return null;
    const ref = `text:${cfg.id}`;
    const scaledSize = Number(cfg.fontSize || 12) * fontScale;
    const content = resolveCustomText(contentJson, cfg.id, language);
    return (
      <div
        key={ref}
        className={`${styles.element} ${styles.textElement} ${styles.customTextElement} ${selectedElement === ref ? styles.elementSelected : ''}`}
        style={{
          left: `${clamp(cfg.x, 0, 100)}%`, top: `${clamp(cfg.y, 0, 100)}%`,
          width: `${clamp(cfg.width, 0, 100)}%`, height: `${clamp(cfg.height, 0, 100)}%`,
          color: cfg.color || '#fff', fontWeight: cfg.fontWeight || 800,
          minWidth: interactive ? 14 : 1, minHeight: interactive ? 10 : 1,
          cursor: interactive ? 'grab' : 'default', ...alignStyle(cfg.align),
        }}
        {...interactionProps(ref)}
      >
        <PreviewAutoFitText maxSize={scaledSize} minSize={Math.max(3.5, scaledSize * .48)} align={cfg.align || 'left'}>{content}</PreviewAutoFitText>
      </div>
    );
  };

  const renderImageElement = (cfg) => {
    if (!cfg || cfg.visible === false || imageOnly) return null;
    const ref = `image:${cfg.id}`;
    const asset = assetMap[cfg.assetId] || {};
    const src = assetPreviewUrls[cfg.assetId] || asset.url || '';
    if (!src) return null;
    return (
      <div
        key={ref}
        className={`${styles.element} ${styles.imageElement} ${selectedElement === ref ? styles.elementSelected : ''}`}
        style={{
          left: `${clamp(cfg.x, 0, 100)}%`, top: `${clamp(cfg.y, 0, 100)}%`,
          width: `${clamp(cfg.width, 0, 100)}%`, height: `${clamp(cfg.height, 0, 100)}%`,
          opacity: clamp(cfg.opacity ?? 100, 0, 100) / 100,
          transform: `rotate(${clamp(cfg.rotation ?? 0, -180, 180)}deg)`,
          minWidth: interactive ? 14 : 1, minHeight: interactive ? 10 : 1,
          cursor: interactive ? 'grab' : 'default',
        }}
        {...interactionProps(ref)}
      >
        <img src={src} alt="" draggable={false} style={{ objectFit: cfg.fit || 'contain' }} />
      </div>
    );
  };

  return (
    <div
      ref={canvasRef}
      className={`${styles.canvas} ${className}`}
      style={{ aspectRatio: ratio }}
      dir={rtl ? 'rtl' : 'ltr'}
      data-image-only={imageOnly ? 'true' : 'false'}
      onPointerDown={() => interactive && onSelectElement?.(null)}
    >
      <div className={styles.fallbackBackground} />
      {imageUrl ? (
        <img
          className={styles.background}
          src={imageUrl}
          alt=""
          draggable={false}
          style={{
            objectFit: String(backgroundMode || 'COVER').toLowerCase(),
            objectPosition: `${background.positionX ?? 50}% ${background.positionY ?? 50}%`,
            opacity: clamp(background.opacity ?? 100, 0, 100) / 100,
            transform: `scale(${clamp(background.scale ?? 100, 50, 250) / 100})`,
          }}
        />
      ) : <div className={styles.emptyImage}>{t('배경 이미지 미리보기')}</div>}
      {!imageOnly ? <div className={styles.overlay} style={{ opacity: clamp(background.overlayOpacity ?? 18, 0, 100) / 100 }} /> : null}
      {(branch.imageElements || []).map(renderImageElement)}
      {interactive ? <div className={styles.safeArea} /> : null}
      {renderFixedElement('badge')}
      {renderFixedElement('title', <span>{translation.title}<em className={styles.titleAccent}>{translation.titleAccent}</em></span>)}
      {renderFixedElement('reward', <span>{translation.reward}</span>)}
      {renderFixedElement('description', <span>{translation.description}</span>)}
      {renderFixedElement('button')}
      {(branch.customTexts || []).map(renderCustomText)}
    </div>
  );
}

function RangeField({ label, value, min, max, step = 1, onChange, suffix = '' }) {
  return (
    <div className={styles.field}>
      <label className={styles.label}>{label}</label>
      <div className={styles.rangeRow}>
        <input className={styles.range} type="range" min={min} max={max} step={step} value={value} onChange={(e) => onChange(Number(e.target.value))} />
        <span className={styles.rangeValue}>{Number(value).toFixed(step < 1 ? 1 : 0)}{suffix}</span>
      </div>
    </div>
  );
}

function defaultCustomTextConfig(id, index, device) {
  return {
    id,
    x: device === 'mobile' ? 20 : 11,
    y: clamp(12 + (index * 20), 4, 78),
    width: device === 'mobile' ? 48 : 34,
    height: 18,
    fontSize: device === 'mobile' ? 10 : 13,
    visible: true,
    align: 'left',
    color: '#ffffff',
    fontWeight: 850,
  };
}

function defaultImageConfig(id, assetId, index, device) {
  return {
    id,
    assetId,
    x: device === 'mobile' ? 72 : 73,
    y: clamp(8 + (index * 8), 4, 60),
    width: device === 'mobile' ? 24 : 14,
    height: 82,
    opacity: 100,
    rotation: 0,
    fit: 'contain',
    visible: true,
  };
}

export default function AdLayoutEditor({
  form,
  previewUrl,
  onChange,
  assetPreviewUrls = {},
  onAddElementImage,
  onRemoveElementImage,
  profileOptions = null,
  previewDevice = null,
}) {
  const device = previewDevice === 'mobile' || previewDevice === 'desktop'
    ? previewDevice
    : (form.device === 'mobile' ? 'mobile' : 'desktop');
  const profiles = useMemo(() => {
    const custom = profileOptions?.[device];
    return Array.isArray(custom) && custom.length ? custom : getProfilesForDevice(device);
  }, [device, profileOptions]);
  const [profile, setProfile] = useState(profiles[0].value);
  const [language, setLanguage] = useState(() => normalizeContentJson(form.contentJson).defaultLanguage || 'ko');
  const [selectedElement, setSelectedElement] = useState('title');
  const [previewZoom, setPreviewZoom] = useState(device === 'mobile' ? 140 : 180);
  const [applyNotice, setApplyNotice] = useState('');

  useEffect(() => {
    setProfile(profiles[0]?.value || getProfilesForDevice(device)[0].value);
    setPreviewZoom(device === 'mobile' ? 140 : 180);
  }, [device, profiles]);

  const contentJson = useMemo(() => normalizeContentJson(form.contentJson), [form.contentJson]);
  const layoutJson = useMemo(() => normalizeLayoutJson(form.layoutJson), [form.layoutJson]);
  const branch = getLayoutBranch(layoutJson, device, profile);
  const selectedConfig = selectedElement ? findElementConfig(branch, selectedElement) : null;
  const selectedType = selectedElement ? elementRefType(selectedElement) : null;
  const selectedId = selectedElement ? elementRefId(selectedElement) : null;
  const selectedCustom = selectedType === 'customText' ? contentJson.customTexts?.[selectedId] : null;
  const selectedAsset = selectedType === 'image' ? layoutJson.assets.find((item) => item.id === selectedConfig?.assetId) : null;

  const setContent = (nextContent) => onChange({ contentJson: nextContent });
  const setLayout = (nextLayout) => onChange({ layoutJson: nextLayout });
  const updateBranch = (nextBranch) => setLayout(updateLayoutBranch(layoutJson, device, profile, nextBranch));
  const updateBackground = (patch) => updateBranch({ ...branch, background: { ...branch.background, ...patch } });

  const updateElement = (ref, patch) => {
    const type = elementRefType(ref);
    const id = elementRefId(ref);
    if (type === 'customText') {
      updateBranch({ ...branch, customTexts: (branch.customTexts || []).map((item) => item.id === id ? { ...item, ...patch } : item) });
      return;
    }
    if (type === 'image') {
      updateBranch({ ...branch, imageElements: (branch.imageElements || []).map((item) => item.id === id ? { ...item, ...patch } : item) });
      return;
    }
    updateBranch({ ...branch, [id]: { ...branch[id], ...patch } });
  };

  const updateBulkField = (fieldKey, value) => {
    setContent({ ...contentJson, bulkFields: { ...contentJson.bulkFields, [fieldKey]: value } });
    setApplyNotice(t('입력 내용이 미리보기에 자동 반영됨'));
  };

  const applyAllBulkTexts = () => {
    const normalized = normalizeContentJson(contentJson);
    setContent(normalized);
    const languageSet = new Set();
    Object.keys(normalized.translations || {}).forEach((key) => languageSet.add(key));
    Object.values(normalized.customTexts || {}).forEach((item) => Object.keys(item.translations || {}).forEach((key) => languageSet.add(key)));
    setApplyNotice(t('언어별 문구 적용 완료 · {count}개 언어 감지', { count: languageSet.size || Object.keys(normalized.translations || {}).length }));
  };

  const copyProfile = (sourceProfile) => {
    const source = getLayoutBranch(layoutJson, device, sourceProfile);
    updateBranch(JSON.parse(JSON.stringify(source)));
  };

  const addCustomText = () => {
    const id = makeId('text');
    const index = Object.keys(contentJson.customTexts || {}).length;
    const nextContent = {
      ...contentJson,
      customTexts: {
        ...contentJson.customTexts,
        [id]: {
          id,
          label: t('텍스트 {number}', { number: index + 1 }),
          bulk: '한국어:새 텍스트 | 영어:New text',
          translations: { ko: '새 텍스트', en: 'New text' },
        },
      },
    };
    const nextLayout = mapLayoutProfiles(layoutJson, device, (profileBranch) => ({
      ...profileBranch,
      customTexts: [...(profileBranch.customTexts || []), defaultCustomTextConfig(id, index, device)],
    }));
    setContent(nextContent);
    setLayout(nextLayout);
    setSelectedElement(`text:${id}`);
  };

  const removeCustomText = (id) => {
    const customTexts = { ...contentJson.customTexts };
    delete customTexts[id];
    setContent({ ...contentJson, customTexts });
    let nextLayout = normalizeLayoutJson(layoutJson);
    ['desktop', 'mobile'].forEach((targetDevice) => {
      nextLayout = mapLayoutProfiles(nextLayout, targetDevice, (profileBranch) => ({
        ...profileBranch,
        customTexts: (profileBranch.customTexts || []).filter((item) => item.id !== id),
      }));
    });
    setLayout(nextLayout);
    setSelectedElement('title');
  };

  const updateCustomTextMeta = (id, patch) => {
    const current = contentJson.customTexts?.[id];
    if (!current) return;
    setContent({
      ...contentJson,
      customTexts: { ...contentJson.customTexts, [id]: { ...current, ...patch } },
    });
    setApplyNotice(t('입력 내용이 미리보기에 자동 반영됨'));
  };

  const addImageFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (!String(file.type || '').startsWith('image/')) return alert(t('이미지 파일만 업로드할 수 있어.'));
    if (file.size > 10 * 1024 * 1024) return alert(t('요소 이미지는 파일당 10MB 이하만 가능해.'));
    const added = await onAddElementImage?.(file);
    if (!added?.id) return;
    const imageId = makeId('image');
    const index = layoutJson.assets.length;
    const nextLayout = mapLayoutProfiles({
      ...layoutJson,
      assets: [...layoutJson.assets, { id: added.id, name: file.name, url: added.url || '', path: added.path || '', mimeType: file.type, fileSize: file.size }],
    }, device, (profileBranch) => ({
      ...profileBranch,
      imageElements: [...(profileBranch.imageElements || []), defaultImageConfig(imageId, added.id, index, device)],
    }));
    setLayout(nextLayout);
    setSelectedElement(`image:${imageId}`);
  };

  const removeImageElement = (imageId, assetId) => {
    const nextLayoutBase = {
      ...layoutJson,
      assets: layoutJson.assets.filter((item) => item.id !== assetId),
    };
    const families = ['desktop', 'mobile'];
    let nextLayout = normalizeLayoutJson(nextLayoutBase);
    families.forEach((family) => {
      const targetDevice = family === 'mobile' ? 'mobile' : 'desktop';
      nextLayout = mapLayoutProfiles(nextLayout, targetDevice, (profileBranch) => ({
        ...profileBranch,
        imageElements: (profileBranch.imageElements || []).filter((item) => item.assetId !== assetId && item.id !== imageId),
      }));
    });
    setLayout(nextLayout);
    onRemoveElementImage?.(assetId);
    setSelectedElement('title');
  };

  const elementTitle = selectedType === 'customText'
    ? selectedCustom?.label || t('추가 텍스트')
    : selectedType === 'image'
      ? selectedAsset?.name || t('요소 이미지')
      : t(ELEMENT_LABELS[selectedId] || '요소');

  return (
    <section className={styles.editor}>
      <div className={styles.header}>
        <div>
          <div className={styles.headerTitle}>{t('배너 레이아웃·다국어 편집')}</div>
          <div className={styles.headerHint}>{t('각 텍스트 요소에 언어별 문구를 한 칸에 통으로 입력하고, 배경 외 장식 이미지도 로컬에서 추가할 수 있습니다.')}</div>
        </div>
        <div className={styles.headerControls}>
          <select className={styles.select} value={profile} onChange={(e) => setProfile(e.target.value)}>
            {profiles.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
          </select>
          <select className={styles.select} value={language} onChange={(e) => setLanguage(e.target.value)} title={t('클라이언트 언어별 미리보기')}>
            {AD_LANGUAGES.map((item) => <option key={item.value} value={item.value}>{t('미리보기: {language}', { language: item.label })}</option>)}
          </select>
          <select className={styles.select} value={contentJson.defaultLanguage} onChange={(e) => setContent({ ...contentJson, defaultLanguage: e.target.value })} title={t('번역이 없을 때 사용할 언어')}>
            {AD_LANGUAGES.map((item) => <option key={item.value} value={item.value}>{t('기본: {language}', { language: item.label })}</option>)}
          </select>
          <select className={styles.select} value={previewZoom} onChange={(e) => setPreviewZoom(Number(e.target.value))}>
            {[100, 125, 150, 180, 200, 250].map((value) => <option key={value} value={value}>{t('편집 화면 {value}%', { value })}</option>)}
          </select>
        </div>
      </div>

      <div className={styles.body}>
        <div className={styles.left}>
          <div className={styles.canvasViewport}>
            <div className={styles.canvasStage} style={{ width: `${previewZoom}%` }}>
              <AdLayoutCanvas
                imageUrl={previewUrl}
                contentJson={contentJson}
                layoutJson={layoutJson}
                device={device}
                profile={profile}
                language={language}
                selectedElement={selectedElement}
                onSelectElement={setSelectedElement}
                onElementChange={updateElement}
                interactive
                backgroundMode={form.backgroundMode}
                templateKey={form.templateKey}
                assetPreviewUrls={assetPreviewUrls}
              />
            </div>
          </div>
          <div className={styles.instructions}>{t('확대된 캔버스에서 요소를 끌어 배치합니다. 좌표는 배너 내부 백분율로 저장되어 같은 기기 프로필 안에서 창 크기에 맞춰 함께 축소·확대됩니다.')}</div>

          <div className={styles.section} style={{ marginTop: 12 }}>
            <div className={styles.bulkHeader}>
              <div>
                <div className={styles.sectionTitle}>{t('텍스트 요소별 통합 다국어 입력')}</div>
                <div className={styles.bulkHint}>{t('구분자는 줄바꿈, |, 쉼표를 사용할 수 있습니다. 예: 한국어:지금 바로 | 영어:Right now | 일본어:今すぐ')}</div>
              </div>
              <button type="button" className={styles.applyButton} onClick={applyAllBulkTexts}>{t('언어별 찾아서 적용')}</button>
            </div>
            {applyNotice ? <div className={styles.applyNotice}>{applyNotice}</div> : null}

            <div className={styles.bulkGrid}>
              {AD_FIXED_TEXT_FIELDS.map((field) => (
                <div className={styles.bulkField} key={field.key}>
                  <label className={styles.label}>{t(field.label)}</label>
                  <textarea
                    className={styles.bulkTextarea}
                    value={contentJson.bulkFields?.[field.key] || ''}
                    onChange={(e) => updateBulkField(field.key, e.target.value)}
                    onBlur={applyAllBulkTexts}
                    placeholder={t('한국어:문구 | 영어:Text | 일본어:テキ스트')}
                  />
                </div>
              ))}
            </div>

            <div className={styles.dynamicTextHeader}>
              <div className={styles.sectionTitle}>{t('추가 텍스트 요소')}</div>
              <button type="button" className={styles.smallButtonStrong} onClick={addCustomText}>{t('+ 텍스트 요소 추가')}</button>
            </div>
            {Object.values(contentJson.customTexts || {}).length === 0 ? <div className={styles.emptyState}>{t('추가 텍스트가 없습니다. 필요한 개수만큼 요소를 추가하세요.')}</div> : null}
            <div className={styles.customTextList}>
              {Object.values(contentJson.customTexts || {}).map((item) => (
                <div className={styles.customTextCard} key={item.id}>
                  <div className={styles.customTextTop}>
                    <input className={styles.input} value={item.label} onChange={(e) => updateCustomTextMeta(item.id, { label: e.target.value })} />
                    <button type="button" className={styles.dangerButton} onClick={() => removeCustomText(item.id)}>{t('삭제')}</button>
                  </div>
                  <textarea
                    className={styles.bulkTextarea}
                    value={item.bulk || ''}
                    onChange={(e) => updateCustomTextMeta(item.id, { bulk: e.target.value })}
                    onBlur={applyAllBulkTexts}
                    placeholder={t('한국어:지금 바로 | 영어:Right now')}
                  />
                  <button type="button" className={styles.selectElementButton} onClick={() => setSelectedElement(`text:${item.id}`)}>{t('캔버스에서 이 텍스트 선택')}</button>
                </div>
              ))}
            </div>
          </div>
        </div>

        <aside className={styles.right}>
          <div className={styles.section}>
            <div className={styles.sectionTitle}>{t('배경 이미지')}</div>
            <div className={styles.grid2}>
              <RangeField label={t('가로 중심')} value={branch.background.positionX} min={0} max={100} onChange={(v) => updateBackground({ positionX: v })} suffix="%" />
              <RangeField label={t('세로 중심')} value={branch.background.positionY} min={0} max={100} onChange={(v) => updateBackground({ positionY: v })} suffix="%" />
              <RangeField label={t('확대')} value={branch.background.scale} min={50} max={200} onChange={(v) => updateBackground({ scale: v })} suffix="%" />
              <RangeField label={t('이미지 투명도')} value={branch.background.opacity} min={0} max={100} onChange={(v) => updateBackground({ opacity: v })} suffix="%" />
              <div className={styles.translationWide}><RangeField label={t('텍스트 가독성 오버레이')} value={branch.background.overlayOpacity} min={0} max={100} onChange={(v) => updateBackground({ overlayOpacity: v })} suffix="%" /></div>
            </div>
          </div>

          <div className={styles.section}>
            <div className={styles.sectionTitle}>{t('요소 이미지 업로드')}</div>
            <label className={styles.uploadButton}>{t('+ 로컬 이미지 추가')}<input type="file" accept="image/*" hidden onChange={addImageFile} /></label>
            <div className={styles.sectionHelp}>{t('배경과 별도로 로고, 카드, 칩, 아이콘 등을 여러 개 추가하고 각각 위치·크기를 조절할 수 있습니다.')}</div>
            <div className={styles.assetList}>
              {(branch.imageElements || []).map((item) => {
                const asset = layoutJson.assets.find((candidate) => candidate.id === item.assetId);
                return <button type="button" key={item.id} className={`${styles.assetButton} ${selectedElement === `image:${item.id}` ? styles.elementButtonActive : ''}`} onClick={() => setSelectedElement(`image:${item.id}`)}>{asset?.name || t('이미지 요소')}</button>;
              })}
            </div>
          </div>

          <div className={styles.section}>
            <div className={styles.sectionTitle}>{t('기본 요소 선택')}</div>
            <div className={styles.elementButtons}>
              {AD_ELEMENT_KEYS.map((key) => <button type="button" key={key} className={`${styles.elementButton} ${selectedElement === key ? styles.elementButtonActive : ''}`} onClick={() => setSelectedElement(key)}>{t(ELEMENT_LABELS[key])}</button>)}
            </div>
            {(branch.customTexts || []).length ? <div className={styles.assetList} style={{ marginTop: 8 }}>{(branch.customTexts || []).map((item) => <button type="button" key={item.id} className={`${styles.assetButton} ${selectedElement === `text:${item.id}` ? styles.elementButtonActive : ''}`} onClick={() => setSelectedElement(`text:${item.id}`)}>{contentJson.customTexts?.[item.id]?.label || t('추가 텍스트')}</button>)}</div> : null}
          </div>

          {selectedConfig ? (
            <div className={styles.section}>
              <div className={styles.sectionTitle}>{t('{element} 위치·크기', { element: elementTitle })}</div>
              <div className={styles.grid2}>
                <RangeField label="X" value={selectedConfig.x} min={0} max={100} step={0.1} onChange={(v) => updateElement(selectedElement, { x: v })} suffix="%" />
                <RangeField label="Y" value={selectedConfig.y} min={0} max={100} step={0.1} onChange={(v) => updateElement(selectedElement, { y: v })} suffix="%" />
                <RangeField label={t('너비')} value={selectedConfig.width} min={2} max={100} step={0.1} onChange={(v) => updateElement(selectedElement, { width: v })} suffix="%" />
                <RangeField label={t('높이')} value={selectedConfig.height} min={5} max={100} step={0.1} onChange={(v) => updateElement(selectedElement, { height: v })} suffix="%" />
                {selectedType !== 'image' ? <>
                  <RangeField label={t('기준 글자 크기')} value={selectedConfig.fontSize} min={4} max={48} step={0.5} onChange={(v) => updateElement(selectedElement, { fontSize: v })} suffix="px" />
                  <RangeField label={t('굵기')} value={selectedConfig.fontWeight} min={400} max={950} step={50} onChange={(v) => updateElement(selectedElement, { fontWeight: v })} />
                </> : <>
                  <RangeField label={t('투명도')} value={selectedConfig.opacity ?? 100} min={0} max={100} onChange={(v) => updateElement(selectedElement, { opacity: v })} suffix="%" />
                  <RangeField label={t('회전')} value={selectedConfig.rotation ?? 0} min={-180} max={180} onChange={(v) => updateElement(selectedElement, { rotation: v })} suffix="°" />
                </>}
              </div>
              {selectedType !== 'image' ? (
                <div className={styles.grid2} style={{ marginTop: 10 }}>
                  <div className={styles.field}><label className={styles.label}>{t('정렬')}</label><select className={styles.select} style={{ width: '100%' }} value={selectedConfig.align || 'left'} onChange={(e) => updateElement(selectedElement, { align: e.target.value })}><option value="left">{t('왼쪽')}</option><option value="center">{t('가운데')}</option><option value="right">{t('오른쪽')}</option></select></div>
                  <div className={styles.field}><label className={styles.label}>{t('글자색')}</label><input className={styles.color} type="color" value={selectedConfig.color || '#ffffff'} onChange={(e) => updateElement(selectedElement, { color: e.target.value })} /></div>
                </div>
              ) : (
                <div className={styles.field} style={{ marginTop: 10 }}><label className={styles.label}>{t('이미지 맞춤')}</label><select className={styles.select} style={{ width: '100%' }} value={selectedConfig.fit || 'contain'} onChange={(e) => updateElement(selectedElement, { fit: e.target.value })}><option value="contain">{t('전체 표시 · contain')}</option><option value="cover">{t('영역 채우기 · cover')}</option><option value="fill">{t('강제 맞춤 · fill')}</option></select></div>
              )}
              <label className={styles.checkRow}><input type="checkbox" checked={selectedConfig.visible !== false} onChange={(e) => updateElement(selectedElement, { visible: e.target.checked })} /> {t('클라이언트에 표시')}</label>
              {selectedType === 'customText' ? <button type="button" className={styles.dangerWideButton} onClick={() => removeCustomText(selectedId)}>{t('이 텍스트 요소 삭제')}</button> : null}
              {selectedType === 'image' ? <button type="button" className={styles.dangerWideButton} onClick={() => removeImageElement(selectedId, selectedConfig.assetId)}>{t('이 이미지 요소 삭제')}</button> : null}
            </div>
          ) : null}

          <div className={styles.section}>
            <div className={styles.sectionTitle}>{t('프로필 좌표 복사')}</div>
            <div className={styles.toolbar}>
              {profiles.filter((item) => item.value !== profile).map((item) => <button type="button" className={styles.smallButton} key={item.value} onClick={() => copyProfile(item.value)}>{t('{profile}에서 복사', { profile: item.label.split(' · ')[0] })}</button>)}
            </div>
          </div>
        </aside>
      </div>
    </section>
  );
}
